import * as React from "react";
import clsx from "clsx";
import type { OrgContextOrganization } from "../../lib/organization-context/types";
import { useScrollOverflowEdges } from "../../../ui/components/Table/useScrollOverflowEdges";
import { Button } from "../../../ui/components/Button";
import { OrganizationContextOption } from "./OrganizationContextOption";
import * as s from "./OrgContext.css";

const SELECT_ALL_THRESHOLD = 5;

export interface OrgContextPickerBodyProps {
  availableOrgs: OrgContextOrganization[];
  selectedOrgIds: string[];
  isMobile?: boolean;
  onToggleOrg: (orgId: string) => void;
  onSelectAll: () => void;
  onSoloOrg: (orgId: string) => void;
}

export function OrgContextPickerBody({
  availableOrgs,
  selectedOrgIds,
  isMobile = false,
  onToggleOrg,
  onSelectAll,
  onSoloOrg,
}: OrgContextPickerBodyProps) {
  const listRef = React.useRef<HTMLDivElement>(null);
  const edges = useScrollOverflowEdges(listRef, [availableOrgs.length]);

  const showSelectAll = availableOrgs.length > SELECT_ALL_THRESHOLD;

  return (
    <>
      {/* pickerListWrapper scopes the absolute-positioned fades to the scroll area only */}
      <div className={s.pickerListWrapper}>
        <div
          ref={listRef}
          className={s.pickerList}
          role="listbox"
          aria-multiselectable="true"
        >
          {availableOrgs.map((org) => {
            const selected = selectedOrgIds.includes(org.id);
            const isLastSelected = selected && selectedOrgIds.length <= 1;
            const isSoleSelection = selectedOrgIds.length === 1 && selected;
            return (
              <OrganizationContextOption
                key={org.id}
                org={org}
                selected={selected}
                disabled={isLastSelected}
                isMobile={isMobile}
                onToggle={() => onToggleOrg(org.id)}
                onSolo={() => onSoloOrg(org.id)}
                isSoleSelection={isSoleSelection}
              />
            );
          })}
        </div>
        <div className={clsx(s.pickerFadeTop, edges.top && s.pickerFadeVisible)} aria-hidden />
        <div className={clsx(s.pickerFadeBottom, edges.bottom && s.pickerFadeVisible)} aria-hidden />
      </div>
      {showSelectAll && (
        <Button
          type="button"
          color="primary"
          variant="outline"
          size="sm"
          className={s.pickerSelectAllBtn}
          onClick={onSelectAll}
        >
          Select all ({availableOrgs.length})
        </Button>
      )}
    </>
  );
}
